import React, { useState } from 'react';
import { Modal } from '../UI/Modal';
import { Input } from '../UI/Input';
import { Button } from '../UI/Button';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';

interface SwitchAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  accountName: string;
}

/**
 * Modal for switching accounts with password re-authentication.
 * Superadmins can switch without entering a password.
 */
export function SwitchAccountModal({
  isOpen,
  onClose,
  accountId,
  accountName,
}: SwitchAccountModalProps) {
  const { switchAccount, user } = useAuth();
  const { showToast } = useToast();
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const isSuperadmin = user?.is_superadmin ?? false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isSuperadmin && !password) {
      setError('Please enter your password');
      return;
    }

    try {
      setIsLoading(true);
      // Superadmins still need to call login with password for now,
      // but the backend allows them to access any account
      await switchAccount(accountId, password);
      showToast('Successfully switched account', 'success');
      onClose();
      setPassword('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to switch account';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setPassword('');
      setError('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Switch Account">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Switching to account: <span className="font-semibold">{accountName}</span>
          </p>
          {!isSuperadmin && (
            <p className="text-sm text-gray-500 mb-4">
              For security, please re-enter your password to switch accounts.
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {!isSuperadmin && (
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            autoComplete="current-password"
            disabled={isLoading}
            autoFocus
          />
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Switch Account
          </Button>
        </div>
      </form>
    </Modal>
  );
}
